import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Input,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  HomeOutlined,
  QuestionOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import SEO from './SEO';
import compareTwoValues from '../common/compareTwoValues';
import {
  colourPalette,
  dateFormat,
  filterTimeout,
  notificationTimeout,
  notificationType,
  query100,
  requestStatus,
  scrollHeight,
} from '../common/constants';
import debugLog from '../common/debugLog';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import { db } from '../firebase';
import './style.scss';

export default function Requests({ site }) {
  const [data, setData] = useState([]);
  const [filterDate, setFilterDate] = useState({ end: '', start: '' });
  const [loading, setLoading] = useState(false);
  const [searchColumn, setSearchColumn] = useState('');
  const [searchText, setSearchText] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  const searchInput = useRef(null);
  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'request')}`,
    total: data.length,
  };

  // destructure imports
  const { RangePicker } = DatePicker;
  const { antdDefault, antdError, antdSuccess, highlighted, searchActive } =
    colourPalette;
  const { timeoutInfo, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeInfo, typeSuccess, typeWarning } = notificationType;
  const { approvedStr, pendingsStr, rejectedStr } = requestStatus;
  const { id } = site;

  const filterIconColour = (filtered) => (
    <SearchOutlined style={{ color: filtered ? searchActive : undefined }} />
  );

  const filterMenu = (
    clearFilters,
    close,
    confirm,
    dataIndex,
    handleReset,
    handleSearch,
    inputSearch, // searchInput
    selectedKeys,
    // setSearchColumn,
    // setSearchText,
    setSelectedKeys,
  ) => (
    <div className="search-dialog">
      <Input
        id="search-input"
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(true, confirm, dataIndex, selectedKeys)
        }
        placeholder={`Search ${dataIndex}`}
        ref={inputSearch}
        value={selectedKeys[0]}
      />
      <Space>
        <Button
          className="search-button"
          icon={<SearchOutlined />}
          onClick={() => handleSearch(true, confirm, dataIndex, selectedKeys)}
          size="small"
          type="primary"
        >
          Search
        </Button>
        <Button
          className="search-button"
          onClick={() => handleReset(clearFilters, confirm, dataIndex)}
          size="small"
        >
          Reset
        </Button>
        <Button
          onClick={() => handleSearch(false, confirm, dataIndex, selectedKeys)}
          size="small"
          type="link"
        >
          Filter
        </Button>
        <Button onClick={() => close()} size="small" type="link">
          Close
        </Button>
      </Space>
    </div>
  );

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchRequests = useCallback(
    (dateFilter, siteId) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);

      const requests = [];
      const requestRef = collection(db, 'requests');
      let q = query(requestRef, where('siteId', '==', siteId));

      // add date filters if applicable, otherwise add query limit
      if (dateFilter.start !== '' && dateFilter.end !== '') {
        q = query(
          q,
          where('requestDate', '>=', dateFilter.start),
          where('requestDate', '<=', dateFilter.end),
        );
      } else {
        q = query(q, limit(query100));
      }

      // order query results
      q = query(q, orderBy('requestDate', 'desc'));

      getDocs(q)
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const request = {};
            const dict = doc.data();
            // @@@@@@ confirm fields again @@@@@@@
            request.dateTime = `${dict.requestDate} ${dict.requestTime}`;
            request.description = dict.description;
            request.remarks = dict.remarks;
            request.requestStatus = dict.requestStatus;
            request.requestType = dict.requestType;

            requests.push(request);
          });

          if (requests?.length) {
            openMessage(
              `fetching latest ${requests.length} ${pluralise(requests.length, 'request')}`,
              timeoutSuccess,
              typeSuccess,
            );
          } else {
            console.warn('-- No record');
            openMessage('No record', timeoutInfo, typeInfo);
          }

          setData(requests);
          setLoading(false);
        })
        .catch((error) => {
          setData([]);
          setLoading(false);
          console.warn('-- Error getting documents:', error);
          openMessage('Error getting documents', timeoutWarning, typeWarning);
        });
    },
    [
      openMessage,
      timeoutInfo,
      timeoutSuccess,
      timeoutWarning,
      typeInfo,
      typeSuccess,
      typeWarning,
    ],
  );

  const handleClick = () => {
    debugLog('-- clicked');
  };

  const handleDatePickerChange = (dates) => {
    if (dates === null) {
      setFilterDate({ start: '', end: '' });
    } else {
      const date1 = dayjs(dates[0]).format(dateFormat);
      const date2 = dayjs(dates[1]).format(dateFormat);
      setFilterDate({ start: date1, end: date2 });
    }
  };

  const handleReload = () => {
    fetchRequests(filterDate, id);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    confirm({ closeDropdown: false }); // dropdown stays open after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(''); // reset search terms
  };

  const handleSearch = (closeDropdown, confirm, dataIndex, selectedKeys) => {
    confirm({ closeDropdown }); // whether dropdown should close after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(selectedKeys[0]); // the search terms
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      clearFilters,
      close,
      confirm,
      selectedKeys,
      setSelectedKeys,
    }) =>
      filterMenu(
        clearFilters,
        close,
        confirm,
        dataIndex,
        handleReset,
        handleSearch,
        searchInput,
        selectedKeys,
        // setSearchColumn,
        // setSearchText,
        setSelectedKeys,
      ),
    filterIcon: (filtered) => filterIconColour(filtered),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), filterTimeout);
        }
      },
    },
    render: (text) =>
      searchColumn === dataIndex ? (
        <Highlighter
          autoEscape
          highlightStyle={{ backgroundColor: highlighted, padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'requestStatus',
      align: 'center',
      width: 140,
      render: (status) => {
        switch (status) {
          case 1:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdSuccess}
                icon={<CheckOutlined />}
              >
                {approvedStr.toUpperCase()}
              </Tag>
            );
          case 2:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdError}
                icon={<CloseOutlined />}
              >
                {rejectedStr.toUpperCase()}
              </Tag>
            );
          default:
            return (
              <Button
                className="table button-tag"
                color={antdDefault}
                icon={<QuestionOutlined />}
                onClick={handleClick}
                type="text"
              >
                {pendingsStr.toUpperCase()}
              </Button>
            );
        }
      },
    },
    {
      title: 'Date Time',
      dataIndex: 'dateTime',
      width: 120,
      sortDirections: ['descend', 'ascend'],
      sorter: {
        compare: (a, b) => compareTwoValues(a.dateTime, b.dateTime),
        multiple: 1,
      },
    },
    {
      title: 'Type',
      dataIndex: 'requestType',
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 300,
      ...getColumnSearchProps('description'),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      width: 300,
      ...getColumnSearchProps('responseDescription'),
    },
  ];

  useEffect(
    () => fetchRequests(filterDate, id),
    [fetchRequests, filterDate, id],
  );

  return (
    <>
      <SEO
        description="Manage general requests"
        name="qiwei"
        title="General Requests"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Requests</span>,
            },
          ]}
        />
        <RangePicker
          format={dateFormat}
          id="rangePicker"
          name="rangePicker"
          onChange={handleDatePickerChange}
          showNow
          variant="borderless"
        />
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={handleReload}
          type="link"
        >
          Reload
        </Button>
      </div>
      {contextHolder}
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="dateTime"
        scroll={{ x: 980, y: scrollHeight }}
      />
    </>
  );
}

Requests.propTypes = {
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};
